import { Section } from '../components/Section';
import { RUN_OF_SHOW, type RunOfShowTag } from '../data/runOfShow';

const TAG_STYLES: Record<RunOfShowTag, { bg: string; fg: string; label: string; bordered?: boolean }> = {
  host: { bg: 'rgb(var(--color-ink))', fg: 'rgb(var(--color-bg))', label: 'HOST' },
  panel: { bg: 'rgb(var(--color-beak))', fg: '#fff', label: 'PANEL' },
  debate: { bg: 'rgb(var(--color-ai))', fg: 'rgb(var(--color-ink))', label: 'AI · HUMAN' },
  audience: { bg: 'rgb(var(--color-duck))', fg: 'rgb(var(--color-ink))', label: 'AUDIENCE', bordered: true },
  rapid: { bg: '#dc2626', fg: '#fff', label: 'RAPID' },
};

export function RunOfShow(): JSX.Element {
  const totalMins = RUN_OF_SHOW.reduce((s, r) => s + r.mins, 0);
  return (
    <Section
      label="RUN OF SHOW"
      title={<>75 minutes. Six moves.</>}
      kicker="Tightly produced. Live. No edits in post. Here is exactly what happens, minute by minute, so nobody — bot or human — can wing it."
    >
      {/* skinny stripe */}
      <div className="mb-7 flex items-center gap-3">
        <span className="font-mono text-[11px] tracking-[0.1em] text-ink-soft">0:00</span>
        <div className="flex h-3.5 flex-1 overflow-hidden rounded-full border-2 border-ink bg-paper">
          {RUN_OF_SHOW.map((r, i) => {
            const c = TAG_STYLES[r.tag];
            return (
              <div
                key={i}
                title={`${r.time} — ${r.title}`}
                style={{
                  flex: r.mins,
                  background: c.bg,
                  borderRight: i < RUN_OF_SHOW.length - 1 ? '1.5px solid rgb(var(--color-ink))' : 'none',
                }}
              />
            );
          })}
        </div>
        <span className="font-mono text-[11px] tracking-[0.1em] text-ink-soft">1:15</span>
      </div>

      {/* six moves */}
      <div className="flex flex-col gap-3">
        {RUN_OF_SHOW.map((r, i) => {
          const c = TAG_STYLES[r.tag];
          return (
            <div
              key={i}
              className="grid items-center gap-6 rounded-[14px] border-2 border-ink bg-paper py-5 pl-6 pr-6"
              style={{
                gridTemplateColumns: '52px 130px 1fr 90px',
                boxShadow: '3px 3px 0 rgb(var(--color-ink))',
              }}
            >
              <div className="font-display text-[30px] font-extrabold leading-none text-beak tabular-nums">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div className="font-mono text-[13px] font-semibold tracking-[0.02em] text-ink">{r.time}</div>
              <div>
                <div className="mb-1 flex items-center gap-2.5">
                  <span
                    className="rounded-full font-mono text-[10px] font-bold tracking-[0.1em]"
                    style={{
                      padding: '3px 8px',
                      background: c.bg,
                      color: c.fg,
                      border: c.bordered ? '1.5px solid rgb(var(--color-ink))' : '0',
                    }}
                  >
                    {c.label}
                  </span>
                  <h3 className="m-0 font-display text-xl font-extrabold leading-tight text-ink">{r.title}</h3>
                </div>
                <p className="m-0 max-w-[720px] text-sm leading-snug text-ink-soft">{r.body}</p>
              </div>
              <div className="whitespace-nowrap text-right font-mono text-xs font-bold text-ink-soft">{r.mins} min</div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-2 font-mono text-xs text-ink-soft">
        <span>// Total: {totalMins} minutes. Hard stops on every move.</span>
        <span>Add to calendar · ICS · Google · Outlook</span>
      </div>
    </Section>
  );
}
